import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Share2, Copy, Check } from "lucide-react";
import { useI18n } from "../i18n";
import api from "../api";

/**
 * Sanitize a URL-derived numeric value. Returns `fallback` when:
 *   - the raw value is null/undefined/empty
 *   - the parsed number is NaN or Infinity (covers strings like "NaN",
 *     "Infinity", "abc", "1e9999", and giant inputs that would overflow
 *     downstream multiplications on iOS Safari's stricter IEEE-754 path)
 *   - the parsed number is outside [min, max]; we clamp instead of throwing
 *
 * This is the single chokepoint that protects every downstream calculation
 * from ever seeing a non-finite number on the *initial* render — which was
 * the iOS Safari crash signature (the page rendered once, then a derived
 * `toLocaleString()` on Infinity threw inside a child component).
 */
function safeNum(raw, fallback, min, max) {
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  if (typeof min === "number" && n < min) return min;
  if (typeof max === "number" && n > max) return max;
  return n;
}

/**
 * Nearshoring ROI Calculator
 *
 * Pure client-side math. Inputs sync to URL params so screenshots are
 * reproducible — buyers can share their scenario internally.
 *
 * Cost assumptions (US$, 2025/26 baseline — sources noted inline):
 *   - US manufacturing labor (fully loaded):     $26 / hr  (BLS Mfg Q4 2025)
 *   - Juárez IMMEX labor (fully loaded):         $7.50 / hr (Mexican IMP, fully burdened)
 *   - China labor (fully loaded):                $6.50 / hr (NBS coastal avg)
 *   - Section 301 tariff (US ← CN, most HTS):    25% (List 1-3 baseline; ignore stacking)
 *   - USMCA duty (US ← MX, qualifying):          0%
 *   - Ocean freight CN → US (40' container):     $2,800
 *   - Trucking Juárez → El Paso (FTL):           $400
 *   - Transit time (CN → US warehouse):          35 days
 *   - Transit time (Juárez → El Paso):           1 day (effective, customs cleared)
 *   - Inventory carrying cost (annual):          22%
 */
const COST = {
  laborUS: 26.0,
  laborMX: 7.5,
  laborCN: 6.5,
  tariffCN: 0.25,
  tariffMX: 0.0,
  tariffUS: 0.0,
  freightCN: 2800,   // per 40' container, CN → US warehouse
  freightMX: 400,    // per FTL, Juárez → El Paso
  containerUnitsAssumption: 5000, // assume avg $20k value per container so freight is small fraction
  transitDaysCN: 35,
  transitDaysMX: 1,
  transitDaysUS: 3,
  carryingPct: 0.22,
};

const REGIONS = ["china", "us", "other"];

export default function ROICalculator() {
  const { lang } = useI18n();
  // Defensive: if i18n provider ever yields an unknown language (e.g. legacy
  // localStorage value from an older build), fall back to English copy so
  // `C.kicker` never throws on initial render.
  const C = COPY[lang] || COPY.en;

  // ── URL read (one-time, no React Router subscription) ───────────────
  // We deliberately do NOT use `useSearchParams()` here. The hook subscribes
  // the component to React Router's location context, which means every
  // `setSearchParams(...)` call (or any sibling component's navigation)
  // re-renders this page. On iOS Safari, those cascading re-renders during
  // slider drag tripped an infinite-loop protection and crashed the
  // calculator. Reading the URL once via the native DOM API completely
  // decouples this page from the router's render cycle.
  const initialParamsRef = useRef(null);
  if (initialParamsRef.current === null) {
    try {
      const search = typeof window !== "undefined" ? window.location.search : "";
      initialParamsRef.current = new URLSearchParams(search);
    } catch (_) {
      initialParamsRef.current = new URLSearchParams();
    }
  }
  const initialParams = initialParamsRef.current;

  // Inputs — sanitized + clamped so a shared URL with malformed params
  // (`?spend=abc`, `?lead=`, `?labor=NaN`, `?spend=999999999999`) can never
  // propagate NaN/Infinity into the calc tree on first paint.
  const [spend, setSpend] = useState(() => safeNum(initialParams.get("spend"), 2000000, 50000, 50000000));
  const [region, setRegion] = useState(() => {
    const r = initialParams.get("region");
    return REGIONS.includes(r) ? r : "china";
  });
  const [leadWeeks, setLeadWeeks] = useState(() => safeNum(initialParams.get("lead"), 12, 1, 26));
  const [laborPct, setLaborPct] = useState(() => safeNum(initialParams.get("labor"), 35, 5, 85));
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  // ── URL write (debounced, native history API, no router subscription) ──
  // Why this shape:
  //  • setSearchParams from react-router updates the router's *state*, which
  //    re-renders every component subscribed to useSearchParams. During a
  //    60Hz iOS Safari touch-drag that cascade compounded into a render
  //    storm. `window.history.replaceState` updates the browser URL only —
  //    no React re-renders triggered.
  //  • 250ms debounce coalesces all the slider's mid-drag ticks into a
  //    single history mutation. iOS Safari throttles replaceState past ~100
  //    calls / 30s; debounce keeps us safely under that.
  //  • skipFirstSync prevents touching history during the initial render
  //    pass, when nothing has changed yet.
  const skipFirstSync = useRef(true);
  useEffect(() => {
    if (skipFirstSync.current) {
      skipFirstSync.current = false;
      return;
    }
    const t = setTimeout(() => {
      try {
        if (typeof window === "undefined" || !window.history?.replaceState) return;
        const params = new URLSearchParams();
        params.set("spend", String(spend));
        params.set("region", region);
        params.set("lead", String(leadWeeks));
        params.set("labor", String(laborPct));
        const newUrl =
          window.location.pathname +
          "?" +
          params.toString() +
          (window.location.hash || "");
        window.history.replaceState(window.history.state, "", newUrl);
      } catch (_) {
        /* iOS Safari throws SecurityError if replaceState is called too
           rapidly. Harmless to skip — URL is share-link UX, not state. */
      }
    }, 250);
    return () => clearTimeout(t);
  }, [spend, region, leadWeeks, laborPct]);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  // ── Math ─────────────────────────────────────────────────────────
  // Every output is force-coerced to a finite number via `safeFinite` so a
  // future input bug, an extreme URL param, or a browser-specific math
  // quirk can never propagate Infinity/NaN into `.toLocaleString()` or
  // `.toFixed()` calls in the render tree.
  const m = useMemo(() => {
    const safeFinite = (n, fb = 0) => (Number.isFinite(n) ? n : fb);

    const safe = Math.max(0, safeFinite(Number(spend)));
    const laborShare = Math.min(0.85, Math.max(0, safeFinite(Number(laborPct)) / 100));
    const currentLaborCost = safe * laborShare;
    const nonLaborCost = safe - currentLaborCost;

    // Current rate by region
    const rateByRegion = {
      china: COST.laborCN,
      us: COST.laborUS,
      other: COST.laborUS, // assume US-equivalent for other developed regions
    };
    const currentRate = rateByRegion[region] || COST.laborUS;
    // Implied labor hours = current labor $ / current rate
    const laborHours = currentRate > 0 ? currentLaborCost / currentRate : 0;
    // New labor cost in Juárez
    const newLaborCost = laborHours * COST.laborMX;
    const laborSavings = Math.max(0, currentLaborCost - newLaborCost);

    // Tariff savings (only meaningful if currently sourcing from China)
    const tariffRateBefore =
      region === "china" ? COST.tariffCN : region === "us" ? COST.tariffUS : 0;
    // Tariff is applied to landed cost (proxy: total spend excluding US-side overhead)
    const tariffPaidBefore = safe * tariffRateBefore;
    const tariffPaidAfter = safe * COST.tariffMX;
    const tariffSavings = Math.max(0, tariffPaidBefore - tariffPaidAfter);

    // Freight delta — rough proxy: spend / $20k per container
    const containersPerYear = Math.max(1, safe / 20000);
    const freightBefore = region === "china" ? containersPerYear * COST.freightCN : containersPerYear * 600;
    const freightAfter = containersPerYear * COST.freightMX;
    const freightSavings = Math.max(0, freightBefore - freightAfter);

    // Inventory carrying — based on transit-time reduction (less in-flight inventory)
    const transitBefore =
      region === "china" ? COST.transitDaysCN : region === "us" ? COST.transitDaysUS : 14;
    const transitAfter = COST.transitDaysMX;
    const transitDaysSaved = Math.max(0, transitBefore - transitAfter);
    // (transit days / 365) * spend = in-flight inventory; * 22% annual carrying
    const carryingSavings = (transitDaysSaved / 365) * safe * COST.carryingPct;

    const totalSavings =
      laborSavings + tariffSavings + freightSavings + carryingSavings;
    const savingsPct = safe > 0 ? totalSavings / safe : 0;

    // Lead-time reduction (weeks)
    const currentLead = safeFinite(Number(leadWeeks));
    const leadWeeksAfter = region === "china" ? 4 : region === "us" ? Math.max(2, currentLead - 1) : 4;
    const leadWeeksSaved = Math.max(0, currentLead - leadWeeksAfter);

    return {
      totalSavings: safeFinite(totalSavings),
      savingsPct: safeFinite(savingsPct),
      laborSavings: safeFinite(laborSavings),
      tariffSavings: safeFinite(tariffSavings),
      freightSavings: safeFinite(freightSavings),
      carryingSavings: safeFinite(carryingSavings),
      newLaborCost: safeFinite(newLaborCost),
      currentLaborCost: safeFinite(currentLaborCost),
      transitDaysSaved: safeFinite(transitDaysSaved),
      leadWeeksAfter: safeFinite(leadWeeksAfter),
      leadWeeksSaved: safeFinite(leadWeeksSaved),
      nonLaborCost: safeFinite(nonLaborCost),
      laborHours: safeFinite(laborHours),
    };
  }, [spend, region, leadWeeks, laborPct]);

  // ── Share ────────────────────────────────────────────────────────
  // `window.location.href` is always defined in a browser context, but
  // accessing it inside an SSR/hydration path or a sandboxed iframe (e.g.
  // an in-app browser embedding Corridor Digital) can throw. Guard it.
  let shareUrl = "";
  try {
    if (typeof window !== "undefined" && window.location) {
      shareUrl = window.location.href || "";
    }
  } catch (_) {
    shareUrl = "";
  }
  async function copyShare() {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* noop */
    }
  }

  return (
    <div data-testid="roi-page">
      {/* HERO */}
      <section className="container-cd pt-16 pb-10" style={{ borderBottom: "1px solid var(--line)" }}>
        <div className="overline">{C.kicker}</div>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9] max-w-5xl">
          {C.title}
        </h1>
        <p className="mt-6 max-w-3xl text-base md:text-lg" style={{ color: "var(--ink-2)" }}>
          {C.sub}
        </p>
      </section>

      <div className="container-cd py-12 grid md:grid-cols-12 gap-10">
        {/* INPUTS */}
        <div className="md:col-span-5 space-y-6">
          <div className="overline">{C.inputsKicker}</div>

          {/* Region */}
          <div>
            <label className="overline" style={{ fontSize: 10 }}>{C.region}</label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {REGIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  className={`cd-chip font-mono text-xs ${region === r ? "cd-chip-dark" : "cd-chip-interactive"}`}
                  data-testid={`roi-region-${r}`}
                >
                  {C.regions[r]}
                </button>
              ))}
            </div>
          </div>

          {/* Annual spend */}
          <Field
            label={C.spend}
            hint={C.spendHint}
            value={spend}
            onChange={setSpend}
            min={50000}
            max={50000000}
            step={50000}
            format={(v) => `$${(v).toLocaleString()}`}
            testid="roi-spend"
          />

          {/* Labor share */}
          <Field
            label={C.laborPct}
            hint={C.laborPctHint}
            value={laborPct}
            onChange={setLaborPct}
            min={5}
            max={85}
            step={1}
            format={(v) => `${v}%`}
            testid="roi-labor"
          />

          {/* Current lead time */}
          <Field
            label={C.lead}
            hint={C.leadHint}
            value={leadWeeks}
            onChange={setLeadWeeks}
            min={1}
            max={26}
            step={1}
            format={(v) => `${v} ${C.weeks}`}
            testid="roi-lead"
          />
        </div>

        {/* RESULTS */}
        <div className="md:col-span-7 space-y-6">
          <div className="cd-card p-8" style={{ background: "var(--ink)", color: "white" }}>
            <div className="overline" style={{ color: "var(--orange)" }}>{C.savingsKicker}</div>
            <div className="font-display text-6xl md:text-7xl mt-4 leading-none" data-testid="roi-savings-amount">
              ${Math.round(m.totalSavings).toLocaleString()}
            </div>
            <div className="overline mt-3" style={{ color: "rgba(255,255,255,0.7)" }}>
              {C.perYear} · {(m.savingsPct * 100).toFixed(1)}% {C.ofSpend}
            </div>

            {/* Inline conversion CTA — placed directly below the savings
                number so the highest-intent moment (visitor just saw a
                5–7-figure annual saving) maps to a single one-tap action.
                Matches the orange primary-button style used across the
                site. The lower "Browse Juárez plants" CTA still exists as
                a secondary anchor in the action row below. */}
            <Link
              to="/directory"
              className="btn btn-primary btn-lg mt-6 w-full justify-center"
              data-testid="roi-savings-cta"
            >
              {C.savingsCta} <ArrowRight size={14} />
            </Link>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <Mini v={`$${Math.round(m.laborSavings).toLocaleString()}`} k={C.lLabor} dark />
              <Mini v={`$${Math.round(m.tariffSavings).toLocaleString()}`} k={C.lTariff} dark />
              <Mini v={`$${Math.round(m.freightSavings).toLocaleString()}`} k={C.lFreight} dark />
              <Mini v={`$${Math.round(m.carryingSavings).toLocaleString()}`} k={C.lCarrying} dark />
            </div>
          </div>

          <div className="cd-card p-6 grid grid-cols-2 gap-4">
            <Mini v={`${m.leadWeeksSaved.toFixed(0)} ${C.weeks}`} k={C.lLeadSaved} />
            <Mini v={`${m.transitDaysSaved.toFixed(0)} ${C.days}`} k={C.lTransitSaved} />
            <Mini v={`$${Math.round(m.newLaborCost).toLocaleString()}`} k={C.lNewLabor} />
            <Mini v={stats?.manufacturers_listed ?? "—"} k={C.lPlants} />
          </div>

          {/* Share + CTA */}
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/directory"
              className="btn btn-primary btn-lg flex-1"
              data-testid="roi-cta-directory"
            >
              {C.ctaDirectory} <ArrowRight size={14} />
            </Link>
            <button
              onClick={copyShare}
              className="btn btn-dark btn-lg"
              data-testid="roi-share-copy"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? C.copied : C.share}
            </button>
          </div>

          {/* Assumptions */}
          <details className="cd-card p-5" data-testid="roi-assumptions">
            <summary className="font-heading font-semibold cursor-pointer">
              {C.assumptionsTitle}
            </summary>
            <div className="mt-4 space-y-2 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              {C.assumptions.map((a, i) => <div key={i}>· {a}</div>)}
            </div>
          </details>
        </div>
      </div>

      {/* SOURCE / DISCLAIMER */}
      <section className="container-cd pb-16">
        <p className="font-mono text-xs max-w-3xl" style={{ color: "var(--ink-2)" }}>
          {C.disclaimer}
        </p>
      </section>
    </div>
  );
}

function Field({ label, hint, value, onChange, min, max, step, format, testid }) {
  // Defensive: sanitize EVERY input value so a touch-fired NaN / out-of-range
  // value can never reach the calc tree (which is what white-screens the
  // page on iOS Safari during fast drag).
  const handle = (raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return; // ignore NaN / Infinity
    const clamped = Math.min(max, Math.max(min, n));
    onChange(clamped);
  };
  // Render-time guard: if `value` ever becomes non-finite (defensive — should
  // never happen now that initial state is clamped), fall back to `min` so
  // the controlled input and the formatted label never receive NaN. iOS
  // Safari treats `<input value={NaN}>` more strictly than other engines.
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : min;
  let displayLabel;
  try {
    displayLabel = format(safeValue);
  } catch (_) {
    displayLabel = String(safeValue);
  }
  return (
    <div>
      <label className="overline" style={{ fontSize: 10 }}>{label}</label>
      <div className="mt-2 flex items-baseline justify-between">
        <span className="font-display text-2xl" data-testid={`${testid}-value`}>{displayLabel}</span>
        <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>{hint}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        // React's `onChange` for form inputs maps to the native `input`
        // event, so it already fires on every iOS Safari touch-drag tick.
        // Listening to `onInput` as well was a duplicate handler that on
        // iOS Safari could fire a redundant state update during the initial
        // controlled-input sync, which is one suspect for the initial-load
        // crash. One handler is enough.
        onChange={(e) => handle(e.target.value)}
        className="w-full mt-2"
        // `touch-action: manipulation` prevents iOS double-tap-zoom from
        // hijacking the slider gesture; the range track stays scrubbable.
        style={{ touchAction: "manipulation" }}
        data-testid={`${testid}-slider`}
      />
    </div>
  );
}

function Mini({ v, k, dark }) {
  return (
    <div style={{ borderTop: `1px solid ${dark ? "rgba(255,255,255,0.15)" : "var(--line)"}`, paddingTop: 12 }}>
      <div className="font-display text-2xl">{v}</div>
      <div className="overline mt-1" style={{ color: dark ? "rgba(255,255,255,0.6)" : "var(--ink-2)", fontSize: 9 }}>{k}</div>
    </div>
  );
}

const COPY = {
  en: {
    kicker: "NEARSHORING · ROI CALCULATOR",
    title: "What does moving production to Juárez save you?",
    sub: "Move four sliders. See annual savings vs your current setup — labor, tariffs, freight, in-flight inventory. Math runs live on cost data from BLS, Mexican IMP, USTR Section 301 schedules and FreightWaves.",
    inputsKicker: "YOUR CURRENT SETUP",
    region: "Currently sourcing from",
    regions: { china: "China", us: "US (domestic)", other: "Other" },
    spend: "Annual sourcing spend",
    spendHint: "USD",
    laborPct: "Labor as % of COGS",
    laborPctHint: "typical 25–55%",
    lead: "Current lead time",
    leadHint: "PO to receipt",
    weeks: "weeks",
    days: "days",
    savingsKicker: "ESTIMATED ANNUAL SAVINGS",
    savingsCta: "See verified plants in Juárez →",
    perYear: "per year",
    ofSpend: "of current sourcing spend",
    lLabor: "Labor delta",
    lTariff: "Tariff avoided (USMCA)",
    lFreight: "Freight delta",
    lCarrying: "Inventory carrying",
    lLeadSaved: "Lead-time cut",
    lTransitSaved: "Transit cut",
    lNewLabor: "New labor cost",
    lPlants: "Verified Juárez plants ready to quote",
    ctaDirectory: "Browse Juárez plants",
    share: "Copy share link",
    copied: "Copied!",
    assumptionsTitle: "Assumptions (click to expand)",
    assumptions: [
      "US labor (fully loaded): $26.00 / hr — BLS Manufacturing wages + benefits, Q4 2025",
      "Juárez IMMEX labor (fully loaded): $7.50 / hr — Mexican IMP, fully burdened with benefits/IMSS",
      "China coastal labor (fully loaded): $6.50 / hr — National Bureau of Statistics avg",
      "Section 301 tariff (CN → US): 25% on Lists 1–3 HTS; we ignore stacking from Section 232 / 201",
      "USMCA duty (MX → US): 0% on qualifying goods (regional value content thresholds met)",
      "Ocean freight CN → US (40' container, all-in): $2,800 (FreightWaves 2025 baseline)",
      "Trucking Juárez → El Paso (FTL): $400 customs-cleared",
      "Transit time CN → US: 35 days door-to-door; Juárez → El Paso: 1 day effective",
      "Inventory carrying cost: 22% annual (industry standard finance + storage + obsolescence)",
    ],
    disclaimer:
      "Estimates only. Real savings depend on product complexity, HTS classification, USMCA qualification, plant capacity and contract terms. Quotes are between buyer and manufacturer — Corridor Digital is not a broker.",
  },
  es: {
    kicker: "NEARSHORING · CALCULADORA ROI",
    title: "¿Cuánto te ahorra mover producción a Juárez?",
    sub: "Mueve cuatro controles. Ve el ahorro anual vs tu setup actual — mano de obra, aranceles, flete, inventario en tránsito. Cálculo en vivo con datos de BLS, IMP, USTR Sección 301 y FreightWaves.",
    inputsKicker: "TU SETUP ACTUAL",
    region: "Hoy compras en",
    regions: { china: "China", us: "EE.UU. (doméstico)", other: "Otro" },
    spend: "Gasto anual en abastecimiento",
    spendHint: "USD",
    laborPct: "Mano de obra como % del costo",
    laborPctHint: "típico 25–55%",
    lead: "Lead time actual",
    leadHint: "PO a recepción",
    weeks: "semanas",
    days: "días",
    savingsKicker: "AHORRO ANUAL ESTIMADO",
    savingsCta: "Ver plantas verificadas en Juárez →",
    perYear: "por año",
    ofSpend: "del gasto actual",
    lLabor: "Δ Mano de obra",
    lTariff: "Arancel evitado (USMCA)",
    lFreight: "Δ Flete",
    lCarrying: "Inventario en tránsito",
    lLeadSaved: "Reducción de lead time",
    lTransitSaved: "Reducción de tránsito",
    lNewLabor: "Nuevo costo de mano de obra",
    lPlants: "Plantas verificadas listas para cotizar",
    ctaDirectory: "Ver plantas de Juárez",
    share: "Copiar enlace",
    copied: "¡Copiado!",
    assumptionsTitle: "Supuestos (clic para expandir)",
    assumptions: [
      "Mano de obra EE.UU. (cargada): $26.00 / hr — BLS Manufactura, Q4 2025",
      "Mano de obra Juárez IMMEX (cargada): $7.50 / hr — IMP, con prestaciones e IMSS",
      "Mano de obra costera China (cargada): $6.50 / hr — Oficina Nacional de Estadística",
      "Arancel Sección 301 (CN → EE.UU.): 25% en Listas 1-3 HTS",
      "Arancel USMCA (MX → EE.UU.): 0% en bienes calificados",
      "Flete marítimo CN → EE.UU. (contenedor 40'): $2,800 (FreightWaves 2025)",
      "Camión Juárez → El Paso (FTL): $400 con aduana liberada",
      "Tránsito CN → EE.UU.: 35 días puerta-a-puerta; Juárez → El Paso: 1 día efectivo",
      "Costo de inventario en tránsito: 22% anual (estándar industria)",
    ],
    disclaimer:
      "Solo estimaciones. El ahorro real depende de complejidad del producto, clasificación HTS, calificación USMCA, capacidad y términos. Las cotizaciones son entre comprador y fabricante — Corridor Digital no es intermediario.",
  },
};
